import CalendarRecurrence from '@components/drawers/CalendarRecurrence';
import WolfButton from '@components/ui/WolfButton';
import WolfDropdown, { DropdownSelectedItem } from '@components/ui/WolfDropdown';
import WolfInput from '@components/ui/WolfInput';
import WolfTextarea from '@components/ui/WolfTextarea';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { CalendarTrigger, Trigger } from '@models/trigger.model';
import { getOrganizationUsersEmailSimpleRequest, User } from '@models/user.model';
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// Extend Intl type to include supportedValuesOf (ES2021 feature)
declare global {
  namespace Intl {
    function supportedValuesOf(key: string): string[];
  }
}

interface AddCalendarTriggerFormProps {
  processName: string;
  initialData?: Trigger;
  saving: boolean;
  existingTriggers?: Trigger[];
  onClose: () => void;
  onSave: (calendartrigger?: CalendarTrigger) => void;
}

const AddCalendarTriggerForm: React.FC<AddCalendarTriggerFormProps> = ({
  processName,
  initialData,
  saving,
  existingTriggers = [],
  onClose,
  onSave,
}) => {
  const timezones: string[] = Intl.supportedValuesOf('timeZone');
  const localeCalTrig = useTranslation('calTrig');
  const localeCommon = useTranslation('common');
  const localeProcessForm = useTranslation('processForm');
  const localeActionMessages = useTranslation('actionMessages');

  const dispatch = useDispatch();

  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(null);
  const [repeatInitialData, setRepeatInitialData] = useState<any>(null);
  const [calendarTrigger, setCalendarTrigger] = useState<CalendarTrigger | undefined>(initialData);
  const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
  const [guestsText, setGuestsText] = useState<string>('');
  const [memberInputValue, setMemberInputValue] = useState<string>('');
  const [reminderEnabled, setReminderEnabled] = useState<boolean>(false);
  const [reminderIntervalValue, setReminderIntervalValue] = useState<number>(1);
  const [reminderIntervalUnit, setReminderIntervalUnit] = useState<'MINUTES' | 'HOURS' | 'DAYS'>('HOURS');
  const [pickerOpenCount, setPickerOpenCount] = useState<number>(0);

  // Fetch organization users on mount
  useEffect(() => {
    const fetchOrganizationUsers = async () => {
      try {
        const response = await graphQlClient.request<getOrganizationUsersEmailSimpleRequest>(
          UserGraphQL.queries.getOrganizationUsersEmail
        );
        setOrganizationUsers(response.getOrganizationUsersEmail || []);
      } catch (error) {
        console.error('Error fetching organization users:', error);
      }
    };

    fetchOrganizationUsers();
  }, []);

  useEffect(() => {
    setCalendarTrigger(initialData);

    // Reset reminder state
    setReminderEnabled(false);
    setReminderIntervalValue(1);
    setReminderIntervalUnit('HOURS');

    if (initialData?.triggerData?.recurrence) {
      setRepeatInitialData(initialData?.triggerData.recurrence);
    } else {
      setRepeatInitialData(null);
    }

    // Set timezone in both selectedTimezone state AND calendarTrigger state
    // Use timeZone (capital Z) as primary, timezone as fallback for backward compatibility
    const targetTimezone = initialData?.triggerData?.timeZone || initialData?.triggerData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    setSelectedTimezone(targetTimezone);

    // Seed the timezone in calendarTrigger state
    if (initialData?.triggerData?.timeZone || initialData?.triggerData?.timezone) {
      setCalendarTrigger((prevState) => ({
        ...prevState,
        timezone: targetTimezone,
      }));
    }

    if (initialData?.triggerData?.startTime) {
      if (!isNaN(new Date(initialData?.triggerData.startTime || '').getTime())) {
        // Parse the UTC ISO string and convert to wall-clock time in the target timezone
        // Use timeZone (capital Z) as primary, timezone as fallback for backward compatibility
        const targetTimezone = initialData?.triggerData.timeZone || initialData?.triggerData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        // Parse as UTC (the backend stores in UTC), then convert to the target timezone
        const startDate = DateTime.fromISO(initialData?.triggerData.startTime, { zone: 'utc' }).setZone(targetTimezone);
        // Format without timezone suffix - this gives us the "wall clock" time in that timezone
        const formattedDate = startDate.toFormat("yyyy-MM-dd'T'HH:mm");
        setCalendarTrigger((prevState) => ({
          ...prevState,
          startTime: formattedDate,
        }));
      }
    }
    if (initialData?.triggerData?.endTime) {
      // Parse the UTC ISO string and convert to wall-clock time in the target timezone
      // Use timeZone (capital Z) as primary, timezone as fallback for backward compatibility
      const targetTimezone = initialData?.triggerData.timeZone || initialData?.triggerData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const endDate = DateTime.fromISO(initialData?.triggerData.endTime, { zone: 'utc' }).setZone(targetTimezone);
      if (!isNaN(endDate.toMillis())) {
        const formattedDate = endDate.toFormat("yyyy-MM-dd'T'HH:mm");
        setCalendarTrigger((prevState) => ({
          ...prevState,
          endTime: formattedDate,
        }));
      }
    }

    // Set name and description from initialData if available
    if (initialData?.name) {
      setCalendarTrigger((prevState) => ({
        ...prevState,
        name: initialData.name,
      }));
    } else if (processName) {
      // Set default name for new triggers (when no initialData name exists)
      setCalendarTrigger((prevState) => ({
        ...prevState,
        name: `${processName}`,
      }));
    }

    if (initialData?.triggerData?.description) {
      setCalendarTrigger((prevState) => ({
        ...prevState,
        description: initialData.triggerData.description,
      }));
    }

    if (initialData?.triggerData?.attendees) {
      const emails = initialData.triggerData.attendees.map((attendee: { email: string }) => attendee.email);
      const emailsText = emails.join(', ');
      setGuestsText(emailsText);
      setCalendarTrigger((prevState) => ({
        ...prevState,
        guests: emailsText,
      }));
    }

    // Load reminder configuration if it exists
    if (initialData?.triggerData?.reminder) {
      const reminder = initialData.triggerData.reminder;
      setReminderEnabled(reminder.enabled || false);
      setReminderIntervalValue(reminder.intervalValue || 1);
      setReminderIntervalUnit(reminder.intervalUnit || 'HOURS');
      setCalendarTrigger((prevState) => ({
        ...prevState,
        reminder: reminder,
      }));
    }
  }, [initialData, processName, localeCalTrig]);

  // Separate effect for setting default dates when creating new trigger
  useEffect(() => {
    // Only set defaults if we don't have initial data AND we have a timezone selected
    if (!initialData?.triggerData?.startTime && selectedTimezone && !calendarTrigger?.startTime) {
      // Get current time in the selected timezone
      const now = DateTime.now().setZone(selectedTimezone);

      // Round up to next 15-minute interval (00, 15, 30, 45)
      const minutes = now.minute;
      const roundedMinutes = Math.ceil(minutes / 15) * 15;
      const roundedNow = now.set({ minute: roundedMinutes % 60, second: 0, millisecond: 0 });

      // If rounding pushed us to the next hour, add an hour
      const adjustedNow = roundedMinutes >= 60 ? roundedNow.plus({ hours: 1 }) : roundedNow;

      const defaultStartTime = adjustedNow.toFormat("yyyy-MM-dd'T'HH:mm");
      const defaultEndTime = adjustedNow.plus({ minutes: 30 }).toFormat("yyyy-MM-dd'T'HH:mm");

      setCalendarTrigger((prevState) => ({
        ...prevState,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
        timezone: selectedTimezone,
      }));
    }
  }, [selectedTimezone, initialData, calendarTrigger?.startTime]);

  const handleTimezoneChange = (_event: any, selected: any) => {
    setSelectedTimezone(selected);
    // When timezone changes, keep the displayed local time but update the timezone context
    // This preserves the user's intended local time in the new timezone
    if (calendarTrigger?.startTime) {
      setCalendarTrigger((prevState) => ({
        ...prevState,
        timezone: selected || undefined,
      }));
    }
    if (calendarTrigger?.endTime) {
      setCalendarTrigger((prevState) => ({
        ...prevState,
        timezone: selected || undefined,
      }));
    }
  };

  const handleCalendarTriggerChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const fieldName = event.target.name === 'triggerName' ? 'name' : event.target.name;
    setCalendarTrigger({
      ...calendarTrigger,
      [fieldName]: event.target.value,
    });
  };

  const handleCalendarTriggerRRuleChange = (rrule: any) => {
    setCalendarTrigger({
      ...calendarTrigger,
      rrule,
    });
  };

  const handleCalendarTriggerDateChange = (type: string, value: string | null) => {
    if (type === 'startTime' && value) {
      const startDate = DateTime.fromISO(value, { zone: selectedTimezone || 'UTC' });
      const endDate = startDate.plus({ minutes: 30 });
      const endDateString = endDate.toFormat("yyyy-MM-dd'T'HH:mm");

      setCalendarTrigger({
        ...calendarTrigger,
        startTime: value,
        endTime: endDateString,
        timezone: selectedTimezone || undefined,
      });
    } else {
      setCalendarTrigger({
        ...calendarTrigger,
        [type]: value,
        timezone: selectedTimezone || undefined,
      });
    }
  };

  const handleGuestsTextChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = event.target.value;
    setGuestsText(newValue);
    setCalendarTrigger({
      ...calendarTrigger,
      guests: newValue,
    });
  };

  const handleSelectOrganizationUser = (_event: any, value: string | null) => {
    if (value) {
      // Extract just the email from the value (in case it's formatted)
      const user = organizationUsers.find((u) => u.email === value);
      const email = user?.email || value;

      // Append to existing guests text
      const newGuestsText = guestsText ? `${guestsText}, ${email}` : email;
      setGuestsText(newGuestsText);
      setCalendarTrigger({
        ...calendarTrigger,
        guests: newGuestsText,
      });

      // Clear the input
      setMemberInputValue('');
    }
  };

  const handleReminderEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setReminderEnabled(enabled);

    // Update calendarTrigger state
    if (enabled) {
      setCalendarTrigger({
        ...calendarTrigger,
        reminder: {
          enabled: true,
          intervalValue: reminderIntervalValue,
          intervalUnit: reminderIntervalUnit,
        },
      });
    } else {
      setCalendarTrigger({
        ...calendarTrigger,
        reminder: undefined,
      });
    }
  };

  const handleReminderIntervalValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    setReminderIntervalValue(value);

    if (reminderEnabled) {
      setCalendarTrigger({
        ...calendarTrigger,
        reminder: {
          enabled: true,
          intervalValue: value,
          intervalUnit: reminderIntervalUnit,
        },
      });
    }
  };

  const handleReminderIntervalUnitChange = (selectedItems: DropdownSelectedItem[]) => {
    const unit = (selectedItems[0]?.value as 'MINUTES' | 'HOURS' | 'DAYS') || 'HOURS';
    setReminderIntervalUnit(unit);

    if (reminderEnabled) {
      setCalendarTrigger({
        ...calendarTrigger,
        reminder: {
          enabled: true,
          intervalValue: reminderIntervalValue,
          intervalUnit: unit,
        },
      });
    }
  };

  const submit = () => {
    if (!calendarTrigger?.name) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['triggerNameRequired'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    // Check for duplicate trigger name (excluding the current trigger if editing)
    const duplicateTrigger = existingTriggers.find(
      (trigger) => trigger.name === calendarTrigger.name && trigger.triggerId !== initialData?.triggerId
    );

    if (duplicateTrigger) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['triggerNameExists'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    // Validate reminder configuration if enabled
    if (calendarTrigger?.reminder?.enabled) {
      if (!calendarTrigger.reminder.intervalValue || calendarTrigger.reminder.intervalValue <= 0) {
        dispatch(
          appendActionMessage({
            message: localeCalTrig['intervalMustBePositive'],
            type: SnackType.ERROR,
          }),
        );
        return;
      }
    }

    // Ensure timezone is set before saving - use browser's timezone if not set
    const triggerToSave = {
      ...calendarTrigger,
      timezone: calendarTrigger?.timezone || selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    onSave(triggerToSave);
    onClose();
  };

  return (
    <Box>
      <WolfInput fullWidth label={localeProcessForm['processName']} value={processName} disabled className="!mb-8" />

      <WolfInput
        fullWidth
        label={localeCalTrig['triggerOneLine']}
        name="triggerName"
        value={calendarTrigger?.name || ''}
        onChange={handleCalendarTriggerChange}
        className="!mb-8"
        tooltipText={localeCalTrig['triggerOneLineTooltip']}
      />

      <WolfTextarea
        label={localeCalTrig['triggerDetails']}
        placeholder={localeCalTrig['triggerDetailsPlaceholder']}
        name="description"
        className="w-full resize-y !mb-8"
        value={calendarTrigger?.description || ''}
        onChange={handleCalendarTriggerChange}
        secondaryLabel={localeCommon['optional']}
        tooltipText={localeCalTrig['triggerDetailsTooltip']}
      />
      {/* Check: Can be moved upstream if needed in more places */}
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <Box className="flex mb-8 gap-8">
          <WolfInput
            fullWidth
            label={localeCalTrig['start']}
            name="startTime"
            value={calendarTrigger?.startTime}
            onChange={(event) => handleCalendarTriggerDateChange('startTime', event.target.value)}
            type="datetime-local"
            onPickerOpen={() => setPickerOpenCount((c) => c + 1)}
            onPickerClose={() => setPickerOpenCount((c) => c - 1)}
          />

          <WolfInput
            fullWidth
            label={localeCalTrig['end']}
            name="endTime"
            value={calendarTrigger?.endTime}
            onChange={(event) => handleCalendarTriggerDateChange('endTime', event.target.value)}
            type="datetime-local"
            onPickerOpen={() => setPickerOpenCount((c) => c + 1)}
            onPickerClose={() => setPickerOpenCount((c) => c - 1)}
          />
        </Box>
      </LocalizationProvider>
      <Typography variant="body14semibold" gutterBottom>
        {localeCalTrig['timezone']}
      </Typography>
      <Autocomplete
        className="mb-8"
        value={selectedTimezone}
        onChange={handleTimezoneChange}
        options={timezones}
        renderInput={(params) => <TextField {...params} variant="outlined" className="!h-10" />}
        isOptionEqualToValue={(option, value) => option === value}
        filterOptions={(options, { inputValue }) => {
          return options.filter((option) => option.toLowerCase().includes(inputValue.toLowerCase()));
        }}
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            padding: '2px !important',
            height: '40px',
            minHeight: '40px',
          },
          '& .MuiOutlinedInput-root': {
            height: '40px',
          },
        }}
      />
      <Box className="mb-8">
        <CalendarRecurrence
          initialData={repeatInitialData}
          refreshRrule={handleCalendarTriggerRRuleChange}
          timezone={selectedTimezone || undefined}
          startDate={calendarTrigger?.startTime}
        />
      </Box>

      <Box className="mb-4">
        <Typography variant="body14semibold" className="!mb-2">
          {localeCalTrig['selectFromOrganization']}
        </Typography>
        <Autocomplete
          value={null}
          inputValue={memberInputValue}
          onInputChange={(_event, newInputValue) => setMemberInputValue(newInputValue)}
          onChange={handleSelectOrganizationUser}
          options={organizationUsers.map((user) => user.email || '')}
          getOptionLabel={(option) => {
            const user = organizationUsers.find((u) => u.email === option);
            return user ? `${user.firstName} ${user.lastName} (${user.email})` : option;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={localeCalTrig['selectMember']}
              className="!h-10"
            />
          )}
          sx={{
            '& .MuiAutocomplete-inputRoot': {
              padding: '2px !important',
              height: '40px',
              minHeight: '40px',
            },
            '& .MuiOutlinedInput-root': {
              height: '40px',
            },
          }}
        />
      <Box className="mt-6">
        <WolfTextarea
          label={localeCalTrig['assignees']}
          placeholder={localeCalTrig['assigneesPlaceholder']}
          name="guests"
          className="w-full resize-y"
          value={guestsText}
          onChange={handleGuestsTextChange}
          secondaryLabel={localeCommon['optional']}
        />
        </Box>
      </Box>

      {/* Reminder Configuration Section */}
      <Box className="mb-8">
        <FormControlLabel
          control={<Checkbox checked={reminderEnabled} onChange={handleReminderEnabledChange} color="primary" />}
          label={<Typography variant="body14semibold">{localeCalTrig['remindAssignee']}</Typography>}
        />

        {(
          <Box className="mt-4">
            <Typography variant="body14" className="mb-2">
              {localeCalTrig['reminderIntervalValue']}
            </Typography>

            <Box className="flex gap-4 items-center mb-2">
              <WolfInput
                label=""
                type="number"
                value={reminderIntervalValue.toString()}
                onChange={handleReminderIntervalValueChange}
                inputProps={{ min: 1 }}
                boxFullWidth={false}
                className="w-24"
                disabled={!reminderEnabled}
              />

              <WolfDropdown
                label={
                  reminderIntervalUnit === 'MINUTES'
                    ? localeCalTrig['reminderMinutes']
                    : reminderIntervalUnit === 'HOURS'
                    ? localeCalTrig['reminderHours']
                    : localeCalTrig['reminderDays']
                }
                items={[
                  { text: localeCalTrig['reminderMinutes'], value: 'MINUTES' },
                  { text: localeCalTrig['reminderHours'], value: 'HOURS' },
                  { text: localeCalTrig['reminderDays'], value: 'DAYS' },
                ]}
                initialSelectedItems={[reminderIntervalUnit]}
                singleSelect={true}
                onChange={handleReminderIntervalUnitChange}
                className="w-40"
                disabled={!reminderEnabled}
              />
            </Box>

            <Typography variant="caption" className="text-textSecondary">
              {localeCalTrig['reminderHelpText']}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className="absolute border-solid border-l-0 border-r-0 border-b-0 border-primaryFrozen !h-16 bottom-0 left-0 w-full bg-white">
        <Box
          className="mt-auto bg-white flex justify-between items-center"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}>
          <WolfButton variant="outlined" color="secondary" onClick={onClose}>
            {localeCommon['cancel']}
          </WolfButton>
          <WolfButton variant="contained" color="primary" disabled={saving || pickerOpenCount > 0} onClick={submit}>
            {localeCommon['save']}
          </WolfButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AddCalendarTriggerForm;
